import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { programUISettingsService, canReadProgramUISettings } from '@/modules/programs/services/programUISettingsService';
import { useAuth } from './AuthContext';
import { PublicProgramUISettingsDto, DEFAULT_UI_SETTINGS } from '@/modules/programs/types/programUISettings';
import { colorTokenToCss, colorTokenToHslComponents } from '@/modules/shared/utils/colorTokens';

interface ProgramThemeContextType {
  settings: PublicProgramUISettingsDto | null;
  isLoading: boolean;
  error: string | null;
  programCode: string | null;
  programId: string | null;
  refreshSettings: () => Promise<void>;
  applyThemeColors: () => void;
}

const ProgramThemeContext = createContext<ProgramThemeContextType | undefined>(undefined);

// GUID regex
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Extract program code from URL (for /p/{CODE}/... login routes)
function extractProgramCodeFromUrl(pathname: string, search: string): string | null {
  const pathMatch = pathname.match(/^\/p\/([A-Z0-9_-]+)/i);
  if (pathMatch) return pathMatch[1].toUpperCase();

  const params = new URLSearchParams(search);
  const programParam = params.get('program');
  if (programParam) return programParam.toUpperCase();

  return null;
}

// Extract programId (GUID) from URL (for /programs/{id}/... authenticated routes)
function extractProgramIdFromUrl(pathname: string): string | null {
  const match = pathname.match(/\/programs\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  return match ? match[1] : null;
}

// Apply CSS custom properties for theme colors
function applyThemeToDocument(settings: PublicProgramUISettingsDto | null) {
  const root = document.documentElement;
  
  if (settings) {
    // Apply primary color
    if (settings.primaryColor) {
      root.style.setProperty('--primary', colorTokenToHslComponents(settings.primaryColor, 'primary', root));
      root.style.setProperty('--program-primary', colorTokenToCss(settings.primaryColor, 'primary'));
    }
    
    // Apply secondary color
    if (settings.secondaryColor) {
      root.style.setProperty('--program-secondary', colorTokenToCss(settings.secondaryColor, 'textMuted'));
    }
    
    // Apply accent color
    if (settings.accentColor) {
      root.style.setProperty('--program-accent', colorTokenToCss(settings.accentColor, 'info'));
    }

    // Apply logo URLs as CSS custom properties for consumption by Layout
    if (settings.internalHeaderLogoUrl) {
      root.style.setProperty('--program-header-logo', `url(${settings.internalHeaderLogoUrl})`);
    }
    if (settings.internalSidebarLogoUrl) {
      root.style.setProperty('--program-sidebar-logo', `url(${settings.internalSidebarLogoUrl})`);
    }
    
    // Apply tab title
    if (settings.tabTitle) {
      document.title = settings.tabTitle;
    }
    
    // Apply favicon
    if (settings.faviconUrl) {
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = settings.faviconUrl;
    }
  } else {
    // Reset to defaults
    root.style.removeProperty('--primary');
    root.style.removeProperty('--program-primary');
    root.style.removeProperty('--program-secondary');
    root.style.removeProperty('--program-accent');
    root.style.removeProperty('--program-header-logo');
    root.style.removeProperty('--program-sidebar-logo');
  }
}

interface ProgramThemeProviderProps {
  children: ReactNode;
}

export function ProgramThemeProvider({ children }: ProgramThemeProviderProps) {
  const location = useLocation();
  const { user, isLoading: isAuthLoading } = useAuth();
  // Known-unauthorized role: skip the request and use the default theme.
  const isRoleKnownUnauthorized = !!user && !canReadProgramUISettings(user.role);
  const [settings, setSettings] = useState<PublicProgramUISettingsDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [programCode, setProgramCode] = useState<string | null>(null);
  const [programId, setProgramId] = useState<string | null>(null);

  // Track the current identifier to prevent stale fetches
  const currentIdentifierRef = useRef<string | null>(null);

  // Fetch by program code (unauthenticated – login pages)
  const fetchByCode = useCallback(async (code: string) => {
    const key = `code:${code}`;
    currentIdentifierRef.current = key;
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await programUISettingsService.getPublicSettingsByCode(code);
      if (currentIdentifierRef.current !== key) return; // stale
      if (response.success && response.data) {
        setSettings(response.data);
        applyThemeToDocument(response.data);
      } else {
        setSettings(null);
        applyThemeToDocument(null);
      }
    } catch (err) {
      if (currentIdentifierRef.current !== key) return;
      console.error('Failed to fetch program UI settings by code:', err);
      setError('Failed to load program theme');
      setSettings(null);
      applyThemeToDocument(null);
    } finally {
      if (currentIdentifierRef.current === key) setIsLoading(false);
    }
  }, []);

  // Fetch by programId GUID (authenticated – internal pages)
  const fetchById = useCallback(async (id: string) => {
    const key = `id:${id}`;
    currentIdentifierRef.current = key;
    setError(null);

    if (isRoleKnownUnauthorized) {
      setSettings(null);
      applyThemeToDocument(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await programUISettingsService.getUISettings(id);
      if (currentIdentifierRef.current !== key) return; // stale
      if (response.success && response.data) {
        // Map ProgramUISettingsDto → PublicProgramUISettingsDto shape
        const mapped: PublicProgramUISettingsDto = {
          tabTitle: response.data.tabTitle,
          faviconUrl: response.data.faviconUrl,
          primaryColor: response.data.primaryColor,
          secondaryColor: response.data.secondaryColor,
          accentColor: response.data.accentColor,
          loginPageLogoUrl: response.data.loginPageLogoUrl,
          internalHeaderLogoUrl: response.data.internalHeaderLogoUrl,
          internalSidebarLogoUrl: response.data.internalSidebarLogoUrl,
          logoAltText: response.data.logoAltText,
          showProgramNameWhenLogoMissing: response.data.showProgramNameWhenLogoMissing,
          increaseFallbackLogoSize: response.data.increaseFallbackLogoSize,
          loginPageTitle: response.data.loginPageTitle,
          loginPageSubtitle: response.data.loginPageSubtitle,
          loginPageDescription: response.data.loginPageDescription,
          loginFeatureCards: response.data.loginFeatureCards,
          loginPageStats: response.data.loginPageStats,
          loginBackgroundType: response.data.loginBackgroundType,
          loginBackgroundValue: response.data.loginBackgroundValue,
          loginBackgroundPatternEnabled: response.data.loginBackgroundPatternEnabled,
        };
        setSettings(mapped);
        applyThemeToDocument(mapped);
      } else {
        setSettings(null);
        applyThemeToDocument(null);
      }
    } catch (err) {
      if (currentIdentifierRef.current !== key) return;
      // Expected 403 is resolved in the service (data: null); anything reaching
      // here is unexpected (5xx, network) and stays logged. Fall back to default theme.
      console.error('Failed to fetch program UI settings by ID:', err);
      setSettings(null);
      applyThemeToDocument(null);
    } finally {
      if (currentIdentifierRef.current === key) setIsLoading(false);
    }
  }, [isRoleKnownUnauthorized]);

  // React to URL changes
  useEffect(() => {
    const code = extractProgramCodeFromUrl(location.pathname, location.search);
    const id = extractProgramIdFromUrl(location.pathname);

    // Priority: program code (login pages) → program ID (authenticated pages)
    if (code) {
      if (code !== programCode) {
        setProgramCode(code);
        setProgramId(null);
        fetchByCode(code);
      }
    } else if (id && GUID_REGEX.test(id)) {
      // Wait for auth so the role is known before deciding whether to call the API.
      if (isAuthLoading) return;
      if (id !== programId) {
        setProgramId(id);
        setProgramCode(null);
        fetchById(id);
      }
    } else {
      // No program context – reset
      if (programCode || programId) {
        setProgramCode(null);
        setProgramId(null);
        currentIdentifierRef.current = null;
        setSettings(null);
        applyThemeToDocument(null);
      }
    }
  }, [isAuthLoading, location.pathname, location.search, programCode, programId, fetchByCode, fetchById]);

  const refreshSettings = useCallback(async () => {
    if (programCode) {
      await fetchByCode(programCode);
    } else if (programId) {
      await fetchById(programId);
    }
  }, [programCode, programId, fetchByCode, fetchById]);

  const applyThemeColors = useCallback(() => {
    applyThemeToDocument(settings);
  }, [settings]);

  return (
    <ProgramThemeContext.Provider
      value={{
        settings,
        isLoading,
        error,
        programCode,
        programId,
        refreshSettings,
        applyThemeColors,
      }}
    >
      {children}
    </ProgramThemeContext.Provider>
  );
}

export function useProgramTheme() {
  const context = useContext(ProgramThemeContext);
  if (context === undefined) {
    throw new Error('useProgramTheme must be used within a ProgramThemeProvider');
  }
  return context;
}

// Hook for accessing theme settings with defaults
export function useProgramThemeSettings() {
  const { settings } = useProgramTheme();
  
  return {
    ...DEFAULT_UI_SETTINGS,
    ...settings,
    // Computed values
    hasCustomSettings: !!settings,
    loginGradient: settings?.loginBackgroundType === 'gradient' || !settings?.loginBackgroundType
      ? `linear-gradient(135deg, ${settings?.primaryColor || DEFAULT_UI_SETTINGS.primaryColor} 0%, ${settings?.secondaryColor || DEFAULT_UI_SETTINGS.secondaryColor} 100%)`
      : settings.loginBackgroundType === 'solid'
        ? settings.loginBackgroundValue
        : `url(${settings.loginBackgroundValue})`,
  };
}
